import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useUserId } from "@/hooks/useUserId";
import { useInspirationSources } from "@/hooks/useInspirationSources";
import { Link, ArrowLeft, Check, Sparkles, Lock } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { extractInstagramUsername, validateInstagramInput, isValidInstagramUsername } from "@/utils/instagramUsername";
import { useProfileVerification, VerifiableProfileInput, ProfileAvatar } from "@/components/onboarding/ProfileVerification";
import VideoTutorialHint from "@/components/onboarding/VideoTutorialHint";

const MAX_SOURCES = 1; // Trial — 1 active source. Pro allows up to 6.
const LOCKED_SLOTS = 2; // Visual-only locked slots shown beneath the active input.
const CONFIRMED_KEY = "onboarding_inspiration_confirmed";

// Returns a stable canonical signature of the verified username set so we
// can decide whether the user has already confirmed this exact lineup.
const buildConfirmedSignature = (usernames: string[]) =>
  JSON.stringify([...usernames].sort());

const STEPS = [
  { id: 1, label: 'Profil' },
  { id: 2, label: 'Branża' },
  { id: 3, label: 'Inspiracje' },
  { id: 4, label: 'Konkurencja' },
  { id: 5, label: 'Płatność' },
  { id: 6, label: 'Start' },
];

const CompetitorAnalysis = () => {
  const [competitors, setCompetitors] = useState<string[]>([""]);
  const [validationErrors, setValidationErrors] = useState<Record<number, string>>({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const userId = useUserId();
  const { sources, validateSourceLimit, fetchSources } = useInspirationSources(userId);
  // After a successful verification, replace the raw input (which may have
  // been a full Instagram URL with query params) with the canonical
  // "@handle" so the row reads cleanly. Skip if the input already matches.
  // Also invalidate any prior confirmation — a fresh verification means
  // the lineup may differ from what the user previously approved.
  const handleVerifySuccess = (index: number, verifiedUsername: string) => {
    setCompetitors(prev => {
      const next = [...prev];
      const canonical = `@${verifiedUsername}`;
      if (next[index] !== canonical) next[index] = canonical;
      return next;
    });
    localStorage.removeItem(CONFIRMED_KEY);
  };
  const { verifications, verifyProfile, clearVerification, removeAndReindex, hasUnverifiedInputs } = useProfileVerification(
    userId,
    "onboarding_inspiration_verifications",
    handleVerifySuccess,
  );

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const saved = localStorage.getItem('onboarding_inspiration_sources');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed.some((c: string) => c.trim() !== "")) {
          setCompetitors(parsed);
        }
      } catch {
        console.error('Failed to parse saved inspiration sources');
      }
    }
  }, []);

  useEffect(() => {
    if (competitors.length > 0) {
      localStorage.setItem('onboarding_inspiration_sources', JSON.stringify(competitors));
    }
  }, [competitors]);

  useEffect(() => {
    if (userId) fetchSources();
  }, [userId, fetchSources]);


  const addCompetitor = () => {
    if (competitors.length < MAX_SOURCES) {
      setCompetitors([...competitors, ""]);
    }
  };

  const removeCompetitor = (index: number) => {
    if (competitors.length > 1) {
      setCompetitors(competitors.filter((_, i) => i !== index));
      removeAndReindex(index);
      // Lineup changed — invalidate the prior confirmation.
      localStorage.removeItem(CONFIRMED_KEY);
    }
  };

  const updateCompetitor = (index: number, value: string) => {
    const updated = [...competitors];
    updated[index] = value.toLowerCase();
    setCompetitors(updated);

    // Clear verification when input changes
    clearVerification(index);
    // Input edited — any prior confirmation no longer applies.
    localStorage.removeItem(CONFIRMED_KEY);

    const errors = { ...validationErrors };
    if (value.trim() !== "") {
      const error = validateInstagramInput(value);
      if (error) {
        errors[index] = error;
      } else {
        delete errors[index];
      }
    } else {
      delete errors[index];
    }
    setValidationErrors(errors);
  };

  const handleContinueClick = () => {
    if (!userId) {
      toast({ title: "Błąd autoryzacji", description: "Musisz być zalogowany", variant: "destructive" });
      return;
    }

    const validCompetitors = competitors.filter(c => c.trim() !== "");

    if (validCompetitors.length === 0) {
      navigate("/onboarding/add-competitors", { replace: true });
      return;
    }

    // Block if any verified profile is blocked (adult)
    const blockedIndices = Object.entries(verifications)
      .filter(([, v]) => v.is_blocked)
      .map(([i]) => Number(i));
    if (blockedIndices.length > 0) {
      toast({ title: "Usuń zablokowane profile", description: "Niektóre profile nie mogą być dodane.", variant: "destructive" });
      return;
    }

    // Deduplicate by extracted username
    const uniqueUsernames = new Set<string>();
    const deduplicatedCompetitors = validCompetitors.filter(vc => {
      const normalized = extractInstagramUsername(vc);
      if (uniqueUsernames.has(normalized)) return false;
      uniqueUsernames.add(normalized);
      return true;
    });

    if (deduplicatedCompetitors.length > MAX_SOURCES) {
      toast({ title: "Za dużo źródeł", description: `Limit to ${MAX_SOURCES} profili.`, variant: "destructive" });
      return;
    }

    const invalidUsernames: string[] = [];
    deduplicatedCompetitors.forEach(input => {
      const username = extractInstagramUsername(input);
      if (!isValidInstagramUsername(username)) {
        invalidUsernames.push(input);
      }
    });
    if (invalidUsernames.length > 0) {
      toast({ title: "Nieprawidłowe nazwy", description: `Sprawdź: ${invalidUsernames.join(', ')}`, variant: "destructive" });
      return;
    }

    // Skip the confirm dialog when the user has already approved this exact
    // lineup of verified usernames. We derive the signature from the
    // verifications state (the authoritative "what's actually verified")
    // rather than from input strings — if the user edits or re-verifies
    // anything, clearVerification + handleVerifySuccess have already
    // invalidated CONFIRMED_KEY, but using verifications as the source of
    // truth defends against any path where the two states drift.
    const verifiedUsernames = Object.values(verifications)
      .filter(v => v.status === "success" && v.username)
      .map(v => v.username as string);
    const currentSignature = buildConfirmedSignature(verifiedUsernames);
    const savedSignature = localStorage.getItem(CONFIRMED_KEY);
    if (savedSignature === currentSignature) {
      navigate("/onboarding/add-competitors", { replace: true });
      return;
    }

    setShowConfirmDialog(true);
  };

  const handleConfirmContinue = async () => {
    const verifiedUsernames = Object.values(verifications)
      .filter(v => v.status === "success" && v.username)
      .map(v => v.username as string);
    localStorage.setItem(
      CONFIRMED_KEY,
      buildConfirmedSignature(verifiedUsernames),
    );
    navigate("/onboarding/add-competitors", { replace: true });
  };

  const validCompetitorsList = competitors.filter(c => c.trim() !== "").map(c => extractInstagramUsername(c));

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FEF3EC] via-[#F9B896] to-[#F5A07A] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-secondary/5 via-transparent to-transparent" />
      <div className="absolute top-1/4 -right-48 w-96 h-96 bg-primary/10 rounded-full blur-[120px] animate-pulse" />
      <div className="absolute bottom-1/4 -left-48 w-96 h-96 bg-secondary/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />

      <div className="relative z-10 max-w-lg mx-auto px-4 py-8 md:py-16">
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((s, index) => (
            <div key={s.id} className="flex items-center gap-2">
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all
                ${s.id < 3 ? 'bg-primary text-primary-foreground' : s.id === 3 ? 'bg-primary text-primary-foreground' : 'bg-white/60 text-gray-500'}
              `}>
                {s.id < 3 ? <Check className="w-4 h-4" /> : s.id}
              </div>
              {index < STEPS.length - 1 && <div className={`w-8 h-0.5 ${s.id < 3 ? 'bg-primary' : 'bg-gray-300'}`} />}
            </div>
          ))}
        </div>

        <div className="flex justify-center mb-6">
          <VideoTutorialHint />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="bg-white/80 rounded-2xl shadow-lg border border-white/60 p-6 md:p-8"
        >
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100/50 text-sm text-gray-500 mb-4">
              <span>Opcjonalne</span>
            </div>
            <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-primary to-primary/80 rounded-2xl flex items-center justify-center shadow-lg">
              <Sparkles className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight leading-tight text-gray-900 mb-2">Kto Cię inspiruje?</h1>
            <p className="text-gray-500 leading-relaxed">Twoje źródła pomysłów</p>
          </div>

          <div className="space-y-3">
            {competitors.map((competitor, index) => (
              <VerifiableProfileInput
                key={index}
                index={index}
                value={competitor}
                onChange={updateCompetitor}
                onRemove={removeCompetitor}
                onVerify={verifyProfile}
                onEnterAdd={addCompetitor}
                verification={verifications[index]}
                validationError={validationErrors[index]}
                canRemove={competitors.length > 1}
                icon={<Link className="h-4 w-4" />}
                totalCount={competitors.length}
              />
            ))}

            {Array.from({ length: LOCKED_SLOTS }).map((_, i) => (
              <div
                key={`locked-${i}`}
                aria-hidden="true"
                className="flex items-center gap-2 w-full h-11 px-3 rounded-xl border border-dashed border-gray-300 bg-gray-100/60 text-gray-400 select-none"
              >
                <Lock className="h-4 w-4" />
                <span className="text-sm">Po aktywacji</span>
              </div>
            ))}
          </div>

          <Button
            onClick={handleContinueClick}
            className="w-full h-12 text-base font-medium mt-6"
            size="lg"
            disabled={Object.keys(validationErrors).length > 0 || hasUnverifiedInputs(competitors)}
          >
            Kontynuuj
          </Button>

          {hasUnverifiedInputs(competitors) && (
            <p className="text-center text-sm text-destructive mt-2">
              Sprawdź wszystkie profile przed kontynuacją
            </p>
          )}

          <div className="mt-4 pt-4 border-t border-gray-200/60">
            <Button variant="ghost" onClick={() => navigate("/onboarding/industry")} className="w-full text-gray-500 hover:text-gray-900">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Wstecz
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="max-w-md bg-white/95 border border-white/70 shadow-2xl rounded-2xl">
          <AlertDialogHeader>
            <div className="flex items-center justify-center mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-primary to-primary/80 rounded-2xl flex items-center justify-center shadow-md">
                <Sparkles className="h-7 w-7 text-white" />
              </div>
            </div>
            <AlertDialogTitle className="text-center text-xl font-semibold tracking-tight text-gray-900">
              Potwierdź profile
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-center space-y-4">
                <div className="bg-white/60 rounded-xl p-4 my-4 border border-white/80 shadow-sm">
                  <ul className="space-y-2">
                    {validCompetitorsList.map((username, index) => {
                      const v = Object.values(verifications).find(ver => ver.username === username);
                      return (
                        <li key={index} className="flex items-center justify-center gap-2 text-base font-medium text-gray-900">
                          {v?.profile_pic_url ? (
                            <ProfileAvatar url={v.profile_pic_url} fullName={v.full_name} size={24} />
                          ) : (
                            <div className="w-5 h-5 bg-primary/10 rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-primary" />
                            </div>
                          )}
                          @{username}
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <p className="text-sm text-gray-500">
                  Czy nazwy profili są poprawne?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 mt-2 sm:!justify-between sm:!space-x-0">
            <AlertDialogCancel className="w-full sm:w-auto rounded-xl bg-white/60 border border-gray-200/60 text-gray-700 hover:bg-primary/10 hover:border-primary/30 hover:text-primary shadow-sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Popraw
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmContinue} className="w-full sm:w-auto rounded-xl shadow-sm">
              Tak, kontynuuj
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CompetitorAnalysis;
